import { useNavigate, useParams } from 'react-router-dom';
import { Text, Box, Group, ThemeIcon, Center, CenterProps, Stack } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import { useQuery } from '@apollo/client';

import { GET_LAST_STEP } from '@queries/trip-profiles';
import { GET_ITINERARY } from '@queries/itinerary';
import LoadingData from '@components/graphql-loading-data';
import { useEffect, useState } from 'react';
// import classes from './breadcrumb.module.css';

const ACTIVE_COLOR = 'var(--mantine-color-voyOrange-6)';
const INACTIVE_COLOR = 'var(--mantine-color-voyCerise-6)';

interface BreadcrumbProps extends CenterProps {
  pageStep: number;
}

/*
 * lastStep is the lastStep via the data
 * pageStep is the step that the page belongs to, passed from page
 * TODO: currentStep is the current step in the process
 */
function Breadcrumb({ pageStep, ...centerProps }: BreadcrumbProps) {
  const navigate = useNavigate();
  const { clientId, tripProfileId } = useParams();
  const [currentStep, setCurrentStep] = useState<number>(0);
  const { loading, error, data } = useQuery(GET_LAST_STEP, {
    variables: {
      id: tripProfileId || '',
    },
    skip: !tripProfileId,
    errorPolicy: 'all',
  });
  // hack for itinerary, just check cache
  const { data: itineraryCache } = useQuery(GET_ITINERARY, {
    variables: { tripProfileId: tripProfileId || '' },
    fetchPolicy: 'cache-only',
  });

  useEffect(() => {
    // either the last step from the data or use the page step
    if (data?.singleTripProfile.lastStep) {
      setCurrentStep(data.singleTripProfile.lastStep);
    }
  }, [data, pageStep]);

  if (loading) return <LoadingData />;
  if (error) return null;

  const steps = [
    { label: 'Theme', link: `/clients/${clientId}/trip-profiles/${tripProfileId}` },
    { label: 'Map', link: `/clients/${clientId}/trip-profiles/${tripProfileId}/map-results` },
    { label: 'Selection', link: `/clients/${clientId}/trip-profiles/${tripProfileId}/selected` },
    { label: 'Vibe Pick', link: `/clients/${clientId}/trip-profiles/${tripProfileId}/vibe-attractions` },
    { label: 'Vibe Result', link: `/clients/${clientId}/trip-profiles/${tripProfileId}/vibe-results` },
    { label: 'Activity Plan', link: `/clients/${clientId}/trip-profiles/${tripProfileId}/itinerary` },
    { label: 'Itinerary', link: `/clients/${clientId}/trip-profiles/${tripProfileId}/narratives` },
  ];

  const getCircleVariant = (step: number) => {
    if (step === pageStep) return 'outline';
    if (step <= currentStep) return 'filled';
    return 'light';
  };

  const getColor = (pageIndex: number) => {
    if (pageIndex === pageStep) return INACTIVE_COLOR;
    if (pageIndex <= currentStep) return INACTIVE_COLOR;
    return ACTIVE_COLOR;
  };

  const getCheckColor = (pageIndex: number) => {
    if (pageIndex === pageStep) return 'var(--mantine-color-voyCerise-6)';
    return 'white';
  };

  const numberCenter = (pageIndex: number) => (
    <Text size="sm" c={ACTIVE_COLOR}>
      {pageIndex}
    </Text>
  );

  const checkedCenter = (pageIndex: number) => <IconCheck size={18} color={getCheckColor(pageIndex)} stroke={5} />;

  const getCenter = (pageIndex: number) => {
    if (pageIndex === pageStep && pageIndex <= currentStep) return checkedCenter(pageIndex);
    if (pageIndex === pageStep) return numberCenter(pageIndex);
    if (pageIndex <= currentStep) return checkedCenter(pageIndex);
    if (pageIndex > currentStep) return numberCenter(pageIndex);
    return null;
  };

  const isNavigable = (pageIndex: number) => pageIndex !== pageStep && (pageIndex <= currentStep || !!itineraryCache);

  const getCursor = (pageIndex: number) => (isNavigable(pageIndex) ? 'pointer' : 'default');

  // console.log('### pageStep: ', pageStep);
  // console.log('### currentStep: ', currentStep);
  // console.log('### lastStep: ', data?.singleTripProfile.lastStep);
  return (
    <Center {...centerProps}>
      <Group gap={0} align="center">
        {steps.map((step, index) => (
          <Box key={step.label} p={0} style={{ display: 'flex', alignItems: 'center' }}>
            {index > 0 && (
              <Box
                h={3}
                w={56}
                mt={-30}
                bg={currentStep > index ? INACTIVE_COLOR : ACTIVE_COLOR}
                style={{ borderRadius: 999 }}
              />
            )}
            <Stack align="center" gap={5}>
              <ThemeIcon
                mr={20}
                ml={20}
                variant={getCircleVariant(index + 1)}
                color={getColor(index + 1)}
                size="lg"
                radius="xl"
                onClick={() => isNavigable(index + 1) && navigate(step.link)}
                style={{ cursor: getCursor(index + 1) }}
              >
                {getCenter(index + 1)}
              </ThemeIcon>
              <Text>{step.label}</Text>
            </Stack>
          </Box>
        ))}
      </Group>
    </Center>
  );
}

export default Breadcrumb;
